import { useResponsiveProp, useTheme } from '@shopify/restyle';
import { getLocalCacheForImage } from 'app/design/primitives/image';
import { getImageVariantUrl } from 'app/lib/image-variants';
import { Placeholder, PlaceholderLine, ShineOverlay } from 'app/overrides/rn-placeholder';
import React, { memo, useState } from 'react';
import { type DimensionValue, Pressable, type StyleProp, type ViewStyle, View } from 'react-native';
import { Row } from 'app/design/primitives/box';
import { isPast } from 'app/lib/date';
import { intervalToDuration } from 'date-fns';
import type { Listing } from 'app/types/listing';
import { ListingSubtype, ListingType } from 'app/types/listing';
import { isDonationListing } from 'app/lib/listing';
import { isRequestIcon } from 'app/lib/is-request-icon';
import { useUISize } from 'app/utils/web-helpers';
import type { Theme } from 'app/shopify';
import { ListingThumbnailDonationOverlay } from './listing-thumbnail-donation-overlay';
import { ListingThumbnailImageViewer } from './listing-thumbnail-image-viewer';
import { ListingThumbnailMedia } from './listing-thumbnail-media';

interface ListingThumbnailProps
  extends Pick<Listing, 'type'>, Partial<Pick<Listing, 'isDonation' | 'transactionMode'>> {
  width?: DimensionValue;
  height?: DimensionValue;
  size?: number;
  groups?: unknown[]; // We only care about how many there are, not what they are, so unknown it is
  hideCorpoBadge?: boolean;
  canZoomOnPhoto?: boolean;
  subtype?: ListingSubtype;
  before?: Date | string | null;
  skipDonationOverlay?: boolean;
  picture?: string | null;
  numberPicture?: number | null;
  forcedObjectFit?: 'cover' | 'contain';
  isFound?: boolean;
  useOwnCache?: boolean;
  style?: StyleProp<ViewStyle>;
  fullRadius?: boolean;
}

export const getDonationTextForOverlay = (before?: Date | string | null) => {
  const { days: numDaysBefore, months: numMonthsBefore } = intervalToDuration({
    start: Date.now(),
    end: new Date(before ?? Date.now()),
  });
  const i18key =
    !before || isPast(new Date(before))
      ? 'donation.label'
      : numMonthsBefore
        ? 'donation.numMonths'
        : !numDaysBefore
          ? 'donation.lastDay'
          : 'donation.numDays';
  return {
    i18key,
    i18nValues: {
      count: numMonthsBefore || numDaysBefore,
    },
  };
};

function getListingThumbnailPresentation({
  before,
  donationFlag,
  forcedObjectFit,
  fullRadius,
  isFound,
  isLargeUI,
  numberPicture,
  picture,
  size,
  skipDonationOverlay,
  subtype,
  theme,
  transactionMode,
  type,
  useOwnCache,
  width,
}: {
  before?: Date | string | null;
  donationFlag?: boolean;
  forcedObjectFit?: 'cover' | 'contain';
  fullRadius: boolean;
  isFound?: boolean;
  isLargeUI: boolean;
  numberPicture?: number | null;
  picture?: string | null;
  size: number;
  skipDonationOverlay: boolean;
  subtype?: ListingSubtype;
  theme: Theme;
  transactionMode?: Listing['transactionMode'];
  type: Listing['type'];
  useOwnCache: boolean;
  width?: DimensionValue;
}) {
  const isSVG = picture?.includes('.svg');
  const isStockPhoto = picture?.includes('default/media') && !isSVG;
  const pictureUrl = numberPicture ?? getImageVariantUrl(picture, 'small');
  const isService = subtype === ListingSubtype.service;
  const isDonation = isDonationListing({
    isDonation: donationFlag,
    subtype: subtype ?? ListingSubtype.object,
    transactionMode,
  });
  const thumbnailBorderRadius = fullRadius ? size / 2 : theme.borderRadii['radius.md'];
  const isFillMode = width === '100%';
  const resolvedPictureUrl = typeof pictureUrl === 'string' ? pictureUrl : '';
  const viewerUrl =
    useOwnCache && resolvedPictureUrl
      ? (getLocalCacheForImage(resolvedPictureUrl) ?? resolvedPictureUrl)
      : resolvedPictureUrl;

  return {
    contentFit: forcedObjectFit ?? (isLargeUI || isStockPhoto ? 'contain' : 'cover'),
    donationOverlayText: !isDonation || skipDonationOverlay || size <= 50 ? null : getDonationTextForOverlay(before),
    fillModeFrameStyle: isFillMode
      ? {
          width: '100%' as const,
          aspectRatio: 1,
          borderRadius: thumbnailBorderRadius,
          overflow: 'hidden' as const,
        }
      : undefined,
    fontSizeRequestIcon: isFillMode ? 48 : (size * 38) / 70,
    imageBackgroundColor:
      type === ListingType.request && isSVG ? theme.colors['surface.brand.secondary-5.subtle'] : 'transparent',
    isDefaultRequestIcon: isRequestIcon(String(pictureUrl)) || !pictureUrl,
    isFillMode,
    isSVG,
    pictureUrl,
    requestEmoji: isService ? '🙏' : isFound ? '🤝' : '👋',
    thumbnailBorderRadius,
    viewerUrls: [{ url: viewerUrl }],
  };
}

export type ListingThumbnailPresentation = ReturnType<typeof getListingThumbnailPresentation>;

const ListingThumbnailComponent = memo(function ListingThumbnail({
  picture,
  size = 100,
  width,
  height,
  type,
  subtype,
  isDonation: donationFlag,
  transactionMode,
  before,
  forcedObjectFit,
  canZoomOnPhoto = false,
  skipDonationOverlay = false,
  numberPicture,
  isFound,
  useOwnCache = false,
  style,
  fullRadius = false,
}: ListingThumbnailProps) {
  const theme = useTheme<Theme>();
  const [isImageViewerModalOpen, setIsImageViewerModalOpen] = useState(false);
  const { isLargeUI } = useUISize();
  const resolvedSize = Number(height ?? width ?? size) || size;
  const presentation = getListingThumbnailPresentation({
    before,
    donationFlag,
    forcedObjectFit,
    fullRadius,
    isFound,
    isLargeUI,
    numberPicture,
    picture,
    size: resolvedSize,
    skipDonationOverlay,
    subtype,
    theme,
    transactionMode,
    type,
    useOwnCache,
    width,
  });

  return (
    <View
      style={[
        {
          height: presentation.isFillMode ? undefined : (height ?? resolvedSize),
          width: width ?? resolvedSize,
          aspectRatio: presentation.isFillMode ? 1 : undefined,
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: presentation.thumbnailBorderRadius,
          overflow: 'hidden',
          borderWidth: 1.5,
          borderColor: theme.colors['surface.tertiary-neutral'],
        },
        style,
      ]}
    >
      <Pressable
        disabled={!(canZoomOnPhoto && !presentation.isSVG)}
        onPress={() => canZoomOnPhoto && setIsImageViewerModalOpen(true)}
        style={presentation.fillModeFrameStyle}
      >
        <Row style={presentation.fillModeFrameStyle}>
          <ListingThumbnailMedia
            picture={picture}
            presentation={presentation}
            size={resolvedSize}
            useOwnCache={useOwnCache}
          />
          <ListingThumbnailDonationOverlay overlay={presentation.donationOverlayText} size={resolvedSize} />
        </Row>
      </Pressable>
      <ListingThumbnailImageViewer
        imageUrls={presentation.viewerUrls}
        onClose={() => setIsImageViewerModalOpen(false)}
        visible={canZoomOnPhoto ? isImageViewerModalOpen : false}
      />
    </View>
  );
}, areListingThumbnailPropsEqual);

function areListingThumbnailPropsEqual(prevProps: ListingThumbnailProps, nextProps: ListingThumbnailProps) {
  return (
    prevProps.before === nextProps.before &&
    prevProps.canZoomOnPhoto === nextProps.canZoomOnPhoto &&
    prevProps.forcedObjectFit === nextProps.forcedObjectFit &&
    prevProps.fullRadius === nextProps.fullRadius &&
    prevProps.height === nextProps.height &&
    prevProps.isFound === nextProps.isFound &&
    prevProps.isDonation === nextProps.isDonation &&
    prevProps.numberPicture === nextProps.numberPicture &&
    prevProps.picture === nextProps.picture &&
    prevProps.size === nextProps.size &&
    prevProps.skipDonationOverlay === nextProps.skipDonationOverlay &&
    prevProps.subtype === nextProps.subtype &&
    prevProps.transactionMode === nextProps.transactionMode &&
    prevProps.type === nextProps.type &&
    prevProps.useOwnCache === nextProps.useOwnCache &&
    prevProps.width === nextProps.width &&
    prevProps.style === nextProps.style
  );
}

function ListingThumbnailSkeleton({ size }: Pick<ListingThumbnailProps, 'size'>) {
  const resolvedSize = useResponsiveProp(size);

  return (
    <Placeholder Animation={ShineOverlay}>
      <PlaceholderLine
        style={{
          width: resolvedSize,
          height: resolvedSize,
          aspectRatio: 1,
        }}
      />
    </Placeholder>
  );
}

export const ListingThumbnail = Object.assign(ListingThumbnailComponent, {
  Skeleton: ListingThumbnailSkeleton,
});
